import { PencilIcon, PlusCircleIcon, TrashIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import useSWR from 'swr'

import { Loading } from '@/components/Loading'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { apiFetcher } from '@/data/fetcher'
import { ParameterFormModal } from '@/forms/ParameterForm/ParameterFormModal'
import { Parameter } from '@/interfaces/catalog/Parameter'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'

export const Parameters = () => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'catalog',
  })

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingParameter, setEditingParameter] = useState<Parameter | null>(null)
  const [deletingParameter, setDeletingParameter] = useState<Parameter | null>(null)

  const {
    data: response,
    isLoading,
    mutate,
  } = useSWR<{ data?: Parameter[]; total?: number }>(
    '/api/v2/parameters',
    apiFetcher,
  )

  const parameters = response?.data || []

  const openModal = (parameter: Parameter | null) => {
    setEditingParameter(parameter)
    setIsModalOpen(true)
  }

  const onDelete = async () => {
    if (!deletingParameter) return
    try {
      await API.delete(`/api/v2/parameters/${deletingParameter.id}`)
      toast.success(t('parameters.delete.success'))
      mutate()
    } catch (error: unknown) {
      toast.error(t('parameters.delete.error'), {
        description: extractErrorMessage(error) || undefined,
      })
    }
    setDeletingParameter(null)
  }

  if (isLoading) {
    return <Loading />
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex justify-end">
          <Button onClick={() => openModal(null)}>
            <PlusCircleIcon className="size-5" />
            {t('parameters.actions.create')}
          </Button>
        </div>

        <div className="border rounded-lg p-4">
          {parameters.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('parameters.empty')}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('parameters.table.name')}</TableHead>
                  <TableHead>{t('parameters.table.code')}</TableHead>
                  <TableHead>{t('parameters.table.valueType')}</TableHead>
                  <TableHead>{t('parameters.table.roles')}</TableHead>
                  <TableHead>{t('parameters.table.values')}</TableHead>
                  <TableHead className="text-right">{t('parameters.table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {parameters.map((parameter) => (
                  <TableRow key={parameter.id}>
                    <TableCell>{parameter.name}</TableCell>
                    <TableCell>{parameter.code}</TableCell>
                    <TableCell>
                      {t(`parameters.form.valueType.options.${parameter.valueType}`)}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {parameter.isDescriptive && (
                          <Badge variant="secondary">
                            {t('parameters.form.roles.descriptive')}
                          </Badge>
                        )}
                        {parameter.isFilterable && (
                          <Badge variant="secondary">
                            {t('parameters.form.roles.filterable')}
                          </Badge>
                        )}
                        {parameter.isSurcharge && (
                          <Badge variant="secondary">
                            {t('parameters.form.roles.surcharge')}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {parameter.values.map((value) => (
                          <Badge key={value.id} variant="outline">
                            {value.label}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button
                        variant="ghost"
                        size="sm"
                        title={t('parameters.actions.edit')}
                        onClick={() => openModal(parameter)}
                      >
                        <PencilIcon className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        title={t('parameters.actions.delete')}
                        onClick={() => setDeletingParameter(parameter)}
                      >
                        <TrashIcon className="size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <ParameterFormModal
        isOpen={isModalOpen}
        parameter={editingParameter}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => mutate()}
      />

      <AlertDialog
        open={!!deletingParameter}
        onOpenChange={(open) => !open && setDeletingParameter(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('parameters.delete.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('parameters.delete.description', { name: deletingParameter?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('parameters.actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete}>
              {t('parameters.delete.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
